// Trivia Night client helpers shared by the overlay, the VR panel and the big
// screen: the server-clock countdown, sending answers / host moves, and
// small text helpers. Imports only trivia-core (never the question bank).

import { useEffect, useState } from 'react';
import { tableAction, useGames, type TableView } from '../gameStore';
import { categoryLabel, standings, type TriviaState, type TriviaSeatPriv, type TriviaHostPriv, type TriviaMove } from '../../../../supabase/functions/games/trivia-core.ts';

export type { TriviaState };

// ---- clock -------------------------------------------------------------------
// Deadlines in the state are server times. Every state carries `at` (server
// time of the step that made it); local receipt time − at = clock offset +
// latency, and staleness only ever makes it bigger, so the smallest offset
// seen is the best estimate. No skew, no NTP.

let offset: number | null = null;
let lastAt = -1;

/** Record that `g` just arrived (call on every state). */
export function observeServerTime(g: TriviaState | null | undefined) {
  if (!g || typeof g.at !== 'number' || g.at === lastAt) return;
  lastAt = g.at;
  const o = Date.now() - g.at;
  offset = offset === null ? o : Math.min(offset, o);
}
/** A server timestamp in local Date.now() terms. */
export const toLocal = (serverMs: number) => serverMs + (offset ?? 0);

/** ms left on the current phase (frozen while paused), re-rendering ~4×/s. */
export function useTimeLeft(g: TriviaState | null | undefined): number | null {
  const [, force] = useState(0);
  observeServerTime(g);
  const running = !!g && g.pausedLeft === null && g.endsAt !== null && g.phase !== 'over';
  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(() => force((n) => n + 1), 250);
    return () => window.clearInterval(t);
  }, [running, g?.endsAt]);
  if (!g || g.phase === 'over') return null;
  if (g.pausedLeft !== null) return g.pausedLeft;
  if (g.endsAt === null) return null;
  return Math.max(0, toLocal(g.endsAt) - Date.now());
}

// ---- actions -------------------------------------------------------------------

/** Answer the current question; retries if 20 people hit the table at once (409 busy). */
export async function sendAnswer(pid: string, qNo: number, choice: number) {
  for (let i = 0; i < 4; i++) {
    const t = await tableAction(pid, 'move', { move: { type: 'answer', qNo, choice } satisfies TriviaMove });
    if (t) return true;
    if (!/busy/i.test(errorOf(pid) ?? '')) return false;
    await new Promise((r) => setTimeout(r, 120 + Math.random() * 200));
  }
  return false;
}
const errorOf = (pid: string) => useGames.getState().tables[pid]?.error ?? null;

export const hostMove = (pid: string, move: TriviaMove) => tableAction(pid, 'move', { move });

// ---- state helpers ---------------------------------------------------------------

export const triviaOf = (table: TableView | null | undefined) => (table?.state.g ?? null) as TriviaState | null;

/** My pick for the current question (from my private row), or null. */
export function myPick(g: TriviaState | null, priv: unknown): number | null {
  const p = priv as TriviaSeatPriv | null;
  return g && p && typeof p.choice === 'number' && p.qNo === g.qNo ? p.choice : null;
}
/** The right answer for the host (their private row), or null. */
export function hostAnswer(g: TriviaState | null, priv: unknown): number | null {
  const p = priv as TriviaHostPriv | null;
  return g && p && typeof p.answer === 'number' && p.qNo === g.qNo && g.phase === 'question' ? p.answer : null;
}

export function isKicked(table: TableView, seat: number): boolean {
  const g = triviaOf(table);
  const u = table.state.seats[seat]?.userId;
  return !!g && !!u && g.kicked.includes(u);
}

export function optionsLine(o: { categories?: string[]; count?: number; timer?: number; mode?: string } | null | undefined) {
  const cats = o?.categories?.length ? o.categories.map(categoryLabel).join(', ') : 'Mix of everything';
  return `${o?.count ?? 10} questions · ${o?.timer ?? 20} s each · ${o?.mode === 'buzzer' ? 'Buzzer mode' : 'Classic'} · ${cats}`;
}

export const secs = (ms: number) => `${(ms / 1000).toFixed(1)} s`;
export const fmtPts = (n: number) => n.toLocaleString('en-US');
export { standings, categoryLabel };
